/**
 * Live-sequence contact lists: newest first, first page of 5, then
 * incremental load-more. Keeps archive + Firestore name reads cheap even
 * when a step has hundreds of dropouts.
 *
 * A contact "belongs" to a step if they are currently there, dropped there,
 * or already passed through (stepHistory / a later currentStepIndex).
 */

export const STEP_CONTACT_PAGE = 5;

const TERMINAL = new Set(["completed", "replied", "failed", "skipped"]);

export function stepHistoryAt(entry, stepIdx) {
  if (stepIdx == null || !entry?.stepHistory) return null;
  return entry.stepHistory[stepIdx] || entry.stepHistory[String(stepIdx)] || null;
}

export function isHereAtStep(entry, stepIdx) {
  return !!entry && (entry.currentStepIndex || 0) === stepIdx;
}

/** Left this step and moved on (success or skip-and-continue). */
export function isPassedThroughStep(entry, stepIdx) {
  if (!entry || isHereAtStep(entry, stepIdx)) return false;
  if (stepHistoryAt(entry, stepIdx)) return true;
  return (entry.currentStepIndex || 0) > stepIdx;
}

export function belongsToStep(entry, stepIdx) {
  return isHereAtStep(entry, stepIdx) || isPassedThroughStep(entry, stepIdx);
}

/** Timestamp used to sort a row. Prefer when they hit this step. */
export function entryListTime(entry, stepIdx) {
  if (!entry || typeof entry !== "object") return 0;
  const hist = stepHistoryAt(entry, stepIdx);
  if (hist && typeof hist.completedAt === "number") return hist.completedAt;
  if (TERMINAL.has(entry.status) && typeof entry.completedAt === "number") {
    return entry.completedAt;
  }
  if (typeof entry.executeAt === "number") return entry.executeAt;
  if (typeof entry.completedAt === "number") return entry.completedAt;
  if (typeof entry.enteredStepAt === "number") return entry.enteredStepAt;
  return 0;
}

export function sortStepContactPairs(pairs, stepIdx) {
  return [...(pairs || [])].sort((a, b) => {
    const dt = entryListTime(b[1], stepIdx) - entryListTime(a[1], stepIdx);
    if (dt !== 0) return dt;
    return String(a[0]).localeCompare(String(b[0]));
  });
}

export function mergeJobEntries(queueData, archiveData, jobId) {
  const m = new Map();
  Object.entries(archiveData || {}).forEach(([k, e]) => {
    if (e && e.jobId === jobId) m.set(k, e);
  });
  Object.entries(queueData || {}).forEach(([k, e]) => {
    if (e && e.jobId === jobId) m.set(k, e);
  });
  return sortStepContactPairs(Array.from(m.entries()));
}

export function contactsAtStep(pairs, stepIdx, { isCheckReply, repliedGlobal } = {}) {
  const matched = (pairs || []).filter(([, e]) => belongsToStep(e, stepIdx));
  if (!isCheckReply || !Array.isArray(repliedGlobal)) return sortStepContactPairs(matched, stepIdx);
  const have = new Set(matched.map(([k]) => k));
  const extras = repliedGlobal.filter(([k]) => !have.has(k));
  return sortStepContactPairs([...matched, ...extras], stepIdx);
}

export function passedThroughCount(pairs, stepIdx) {
  return (pairs || []).filter(([, e]) => isPassedThroughStep(e, stepIdx)).length;
}

export function pageSizeOrDefault(requested) {
  const n = Number(requested);
  if (!Number.isFinite(n) || n < STEP_CONTACT_PAGE) return STEP_CONTACT_PAGE;
  return Math.floor(n);
}

export function nextPageSize(current) {
  return pageSizeOrDefault(current) + STEP_CONTACT_PAGE;
}

export function visibleContactIds(pairs, visibleCount) {
  const n = pageSizeOrDefault(visibleCount);
  const ids = [];
  const seen = new Set();
  for (const [, e] of (pairs || []).slice(0, n)) {
    const id = e?.contactId;
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export function canLoadMoreContacts(loadedCount, visibleCount, archiveHasMore) {
  const visible = pageSizeOrDefault(visibleCount);
  return loadedCount > visible || archiveHasMore === true;
}
